/**
 * GP Summary — replaces Spreadsheet #1 (Company GP Summary).
 *
 * Per-customer billed, cost, expenses, net GP, and margin for a selected week.
 * Shares ReportShell, week resolution, and export with the other reports.
 */

import { api, type GpSummaryData } from '../api';
import { Card, Cell, ExportButton, Row, Spinner, Table } from '../components/Ui';
import { UnifiedTimeFilter } from '../components/UnifiedTimeFilter';
import { useTimeFilter } from '../context/TimeFilterContext';
import { KpiCard, MarginBadge, MiniBarChart, computeTrend } from '../components/KpiCard';
import { useAsyncData } from '../hooks/useAsyncData';
import { hours, money, moneyCompact, rate, shortDate, toPlotValue } from '../format';
import { Shell, type ShellProps } from '../Shell';
import { ReportShell } from './ReportShell';

export function GpSummary(props: ShellProps) {
  const tf = useTimeFilter();

  const { data, error, loading } = useAsyncData<GpSummaryData>(
    () => api.gpSummary(tf.toWeekEnding()),
    [tf.selection],
    'Could not load GP summary.',
  );

  return (
    <Shell {...props}>
      <ReportShell
        title="GP Summary"
        subtitle="Company-wide gross profit by customer"
        weekEnding={data?.weekEnding}
        actions={
          <>
            {data && data.customers.length > 0 && (
              <ExportButton
                data={data.customers.map((c) => ({
                  customer: c.name,
                  billed: c.billed,
                  loadedCost: c.loadedCost,
                  expenses: c.expenses,
                  subVendor: c.subVendor,
                  netGp: c.netGp,
                  hours: c.hours,
                  contractors: c.contractors,
                  margin: `${c.margin}%`,
                }))}
                filename={`gp-summary-${data.weekEnding ?? 'latest'}`}
                headers={[
                  { key: 'customer', label: 'Customer' },
                  { key: 'billed', label: 'Billed' },
                  { key: 'loadedCost', label: 'Loaded Cost' },
                  { key: 'expenses', label: 'Expenses' },
                  { key: 'subVendor', label: 'Sub-Vendor' },
                  { key: 'netGp', label: 'Net GP' },
                  { key: 'hours', label: 'Hours' },
                  { key: 'contractors', label: 'Contractors' },
                  { key: 'margin', label: 'Margin' },
                ]}
              />
            )}
            <UnifiedTimeFilter />
          </>
        }
      >
        {error && <p className="text-sm text-red-600">{error}</p>}
        {loading && (
          <div className="flex justify-center py-12">
            <Spinner label="Loading GP summary…" />
          </div>
        )}

        {data && data.weekEnding && data.companyTotal && (
          <>
            <GpKpiStrip data={data} />

            <Card>
              <Table
                head={[
                  { label: 'Customer' },
                  { label: 'Contractors', align: 'right' },
                  { label: 'Hours', align: 'right' },
                  { label: 'Billed', align: 'right' },
                  { label: 'Loaded cost', align: 'right' },
                  { label: 'Expenses', align: 'right' },
                  { label: 'Sub-vendor', align: 'right' },
                  { label: 'Net GP', align: 'right' },
                  { label: 'Margin', align: 'right' },
                ]}
              >
                {data.customers.map((c) => (
                  <Row key={c.clientId}>
                    <Cell>
                      <button
                        type="button"
                        onClick={() => props.navigate(`client/${c.clientId}`)}
                        className="font-medium text-ink hover:text-profit"
                      >
                        {c.name}
                      </button>
                    </Cell>
                    <Cell align="right" mono>
                      {c.contractors}
                    </Cell>
                    <Cell align="right" mono>
                      {hours(c.hours)}
                    </Cell>
                    <Cell align="right" mono>
                      {money(c.billed)}
                    </Cell>
                    <Cell align="right" mono muted>
                      {money(c.loadedCost)}
                    </Cell>
                    <Cell align="right" mono muted>
                      {c.expenses !== '0' && c.expenses !== '0.0000' && c.expenses !== '0.00'
                        ? money(c.expenses)
                        : ''}
                    </Cell>
                    <Cell align="right" mono muted>
                      {c.subVendor !== '0' && c.subVendor !== '0.0000' && c.subVendor !== '0.00'
                        ? money(c.subVendor)
                        : ''}
                    </Cell>
                    <Cell align="right" mono>
                      <span className="font-semibold text-profit">{money(c.netGp)}</span>
                    </Cell>
                    <Cell align="right" mono>
                      <span
                        className={
                          Number(c.margin) >= 20
                            ? 'text-profit font-semibold'
                            : Number(c.margin) >= 0
                              ? 'text-ink'
                              : 'text-red-500 font-semibold'
                        }
                      >
                        {c.margin}%
                      </span>
                    </Cell>
                  </Row>
                ))}
                <Row>
                  <Cell>
                    <span className="font-semibold text-ink">Company total</span>
                  </Cell>
                  <Cell align="right" mono>
                    {''}
                  </Cell>
                  <Cell align="right" mono>
                    <span className="font-semibold">{hours(data.companyTotal.hours)}</span>
                  </Cell>
                  <Cell align="right" mono>
                    <span className="font-semibold">{money(data.companyTotal.billed)}</span>
                  </Cell>
                  <Cell align="right" mono>
                    <span className="font-semibold">{money(data.companyTotal.loadedCost)}</span>
                  </Cell>
                  <Cell align="right" mono>
                    <span className="font-semibold">{money(data.companyTotal.expenses)}</span>
                  </Cell>
                  <Cell align="right" mono>
                    <span className="font-semibold">{money(data.companyTotal.subVendor)}</span>
                  </Cell>
                  <Cell align="right" mono>
                    <span className="font-semibold text-profit">
                      {money(data.companyTotal.netGp)}
                    </span>
                  </Cell>
                  <Cell align="right" mono>
                    <span className="font-semibold">{data.companyTotal.margin}%</span>
                  </Cell>
                </Row>
              </Table>
            </Card>
          </>
        )}

        {data && !data.weekEnding && !loading && (
          <Card>
            <p className="px-4 py-8 text-center text-sm text-ink-muted">
              No hours have been imported yet, so there is nothing to summarize.
            </p>
          </Card>
        )}
      </ReportShell>
    </Shell>
  );
}

function FillBar({
  pct,
  left,
  right,
  tone,
}: {
  pct: number;
  left: string;
  right: string;
  tone: 'profit' | 'caution';
}) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="w-full">
      <div className="h-2 w-full overflow-hidden rounded-full bg-surface-overlay">
        <div
          className={`h-full rounded-full ${tone === 'profit' ? 'bg-profit' : 'bg-caution'}`}
          style={{ width: `${clamped.toFixed(1)}%` }}
        />
      </div>
      <div className="mt-0.5 flex justify-between text-[9px] text-ink-faint">
        <span>{left}</span>
        <span>{right}</span>
      </div>
    </div>
  );
}

function ratioPct(numerator: string, denominator: string): number {
  const den = toPlotValue(denominator);
  if (den === 0) return 0;
  return (toPlotValue(numerator) / den) * 100;
}

function perHour(gp: string, hrs: string): number {
  const h = toPlotValue(hrs);
  if (h === 0) return 0;
  return toPlotValue(gp) / h;
}

function GpKpiStrip({ data }: { data: GpSummaryData }) {
  const total = data.companyTotal;
  if (total === null) return null;

  const prior = data.priorWeek;
  const vsPrior = prior !== null ? `vs ${shortDate(prior.weekEnding)}` : undefined;
  const marginPct = Number(total.margin);
  const gpSpark = data.trend.map((t) => toPlotValue(t.gp));
  const billedSpark = data.trend.map((t) => toPlotValue(t.billed));
  const hoursSpark = data.trend.map((t) => toPlotValue(t.hours));
  const marginSpark = data.trend.map((t) => ratioPct(t.gp, t.billed));
  const gpPerHourSpark = data.trend.map((t) => perHour(t.gp, t.hours));

  const top5 = data.customers.slice(0, 5);
  const topShare = top5.length > 0 ? ratioPct(top5[0]!.netGp, total.netGp) : 0;
  const top5Bars = top5.map((c) => ({ label: c.name, value: toPlotValue(c.netGp) }));

  return (
    <div className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
      <KpiCard
        label="Net Gross Profit"
        value={moneyCompact(total.netGp)}
        exact={money(total.netGp)}
        subtitle={`${moneyCompact(total.netGp)} of ${moneyCompact(total.billed)} billed`}
        tone="profit"
        delay={0}
        trend={prior ? computeTrend(toPlotValue(total.netGp), toPlotValue(prior.gp)) : undefined}
        trendContext={vsPrior}
        sparkline={gpSpark.length > 1 ? gpSpark : undefined}
        sparklineColor="#16a34a"
        sparklineLabel="GP/wk"
        footer={
          <FillBar
            pct={marginPct}
            left={`${moneyCompact(total.netGp)} GP`}
            right={`${moneyCompact(total.loadedCost)} loaded`}
            tone={marginPct >= 20 ? 'profit' : 'caution'}
          />
        }
      >
        <MarginBadge margin={marginPct} />
      </KpiCard>

      <KpiCard
        label="Billed"
        value={moneyCompact(total.billed)}
        exact={money(total.billed)}
        subtitle={`${String(total.customers)} customer${total.customers === 1 ? '' : 's'} · ${String(total.contractors)} contractor${total.contractors === 1 ? '' : 's'}`}
        tone="revenue"
        delay={40}
        trend={
          prior ? computeTrend(toPlotValue(total.billed), toPlotValue(prior.billed)) : undefined
        }
        trendContext={vsPrior}
        sparkline={billedSpark.length > 1 ? billedSpark : undefined}
        sparklineColor="#2563eb"
        sparklineLabel="billed/wk"
      />

      <KpiCard
        label="GP Margin"
        value={`${total.margin}%`}
        subtitle="Net GP ÷ billed"
        tone={marginPct >= 20 ? 'profit' : 'caution'}
        delay={80}
        trend={prior ? computeTrend(marginPct, ratioPct(prior.gp, prior.billed)) : undefined}
        trendContext={vsPrior}
        sparkline={marginSpark.length > 1 ? marginSpark : undefined}
        sparklineColor="#16a34a"
        sparklineLabel="margin %"
      />

      <KpiCard
        label="Avg GP/Hour"
        value={total.gpPerHour === null ? '—' : rate(total.gpPerHour)}
        exact={total.gpPerHour === null ? undefined : rate(total.gpPerHour)}
        delay={120}
        trend={
          prior
            ? computeTrend(perHour(total.netGp, total.hours), perHour(prior.gp, prior.hours))
            : undefined
        }
        trendContext={vsPrior}
        sparkline={gpPerHourSpark.length > 1 ? gpPerHourSpark : undefined}
        sparklineColor="#1c1c1a"
        sparklineLabel="$/hr"
      />

      <KpiCard
        label="Billable Hours"
        value={hours(total.hours)}
        subtitle={`Expenses ${money(total.expenses)}${
          total.subVendor !== '0.00' && total.subVendor !== '0.0000' && total.subVendor !== '0'
            ? ` · sub-vendor ${money(total.subVendor)}`
            : ''
        }`}
        delay={160}
        trend={prior ? computeTrend(toPlotValue(total.hours), toPlotValue(prior.hours)) : undefined}
        trendContext={vsPrior}
        sparkline={hoursSpark.length > 1 ? hoursSpark : undefined}
        sparklineColor="#2563eb"
        sparklineLabel="hrs/wk"
      />

      <KpiCard
        label="Top customer"
        value={top5.length > 0 ? `${topShare.toFixed(0)}%` : '—'}
        subtitle={top5.length > 0 ? top5[0]!.name : 'No customers this week'}
        tone={topShare > 60 ? 'caution' : 'neutral'}
        delay={200}
        footer={
          top5Bars.length > 0 ? (
            <MiniBarChart
              bars={top5Bars}
              height={56}
              formatValue={(v) => moneyCompact(v.toFixed(2))}
            />
          ) : undefined
        }
      />
    </div>
  );
}
